import { presetProjection, type PresetUsage } from '@ayumia/component-runtime';
import type { FieldPresetBody } from '@ayumia/contracts';
import { useEffect, useId, useState } from 'react';
import { text } from '../../../app/i18n';
import { Dialog, DialogActions } from '../../../design-system/Overlay';
import { Icon } from '../../../design-system/icons';
import { widgetWord } from './library';
import { presetBodyFrom, settingLabel } from './presets';
import type { Entry } from './widgets';

/**
 * Fenêtres des préréglages (planches « EditeurV2-Prereglage » et « -PrereglageImpacts ») : « Enregistrer comme
 * préréglage » (ce que le préréglage emporte, où il est proposé, propagation automatique) et « Impacts du
 * changement » (usages montrés **avant** d'enregistrer : « 3 suivent · 2 gardent », ERP-MIG-001). Les usages
 * viennent de `ListFieldPresetUsages` ; sans elle, le poste les calcule depuis les gabarits actifs et le dit.
 */

export interface UsageReport {
  preset_version: string;
  usages: PresetUsage[];
  counts: { forms: number; follows?: number; keeps?: number };
  truncated: boolean;
  /** Qui a calculé : le serveur (`ListFieldPresetUsages`) ou le poste (gabarits actifs relus). */
  computedBy: 'server' | 'client';
}

export interface PresetsPort {
  usages: (presetId: string, proposed?: FieldPresetBody) => Promise<UsageReport>;
  /**
   * Publication d'un préréglage (nouveau ou modifié) par le circuit `ModelChange` 1.2.0 (`target_kind: field_preset`)
   * selon la politique du tenant ; absente hors du Studio (aperçu, laboratoire) : le bouton est grisé avec ce motif.
   */
  save?: (body: FieldPresetBody, presetId?: string) => Promise<void>;
  saveUnavailable: string;
}

export const PRESET_SAVE_UNAVAILABLE = 'Enregistrement des préréglages indisponible ici : ouvrez le formulaire dans le Studio.';

function summary(entry: Entry): Array<{ label: string; detail: string }> {
  const presentation = (entry.presentation ?? {}) as Record<string, unknown>;
  const scan = presentation['scan'] as { sources?: string[]; keyboard?: string } | undefined;
  const widget = widgetWord(presentation['widget'] as string | undefined);
  const details: string[] = [widget];
  if (scan?.sources !== undefined) details.push(scan.sources.length === 2 ? 'caméra et douchette' : scan.sources[0] === 'camera' ? 'caméra' : 'douchette');
  if (scan?.keyboard === 'digits') details.push('chiffres');
  const controls: string[] = [];
  if (entry.field.required === true) controls.push('Obligatoire');
  else if (entry.required_when !== undefined) controls.push('Obligatoire selon une condition');
  const validation = entry.field.validation;
  if (validation?.pattern !== undefined) controls.push('Format');
  if (validation?.min !== undefined || validation?.max !== undefined) controls.push(`Entre ${validation?.min ?? '…'} et ${validation?.max ?? '…'}`);
  if ((entry as { duplicates?: { in_series?: boolean } }).duplicates?.in_series === true) controls.push('Unique dans la liste');
  return [
    { label: 'Le widget et ses options', detail: details.join(' · ') },
    { label: 'Le libellé et l’aide, avec leurs traductions', detail: `${text(entry.field.label, 'fr', '')}${entry.field.description === undefined ? '' : ` · « ${text(entry.field.description, 'fr', '')} »`}` },
    { label: 'Les contrôles', detail: controls.length === 0 ? 'Aucun' : controls.join(' · ') },
  ];
}

export function SavePresetDialog({ entry, port, onClose }: { entry: Entry; port: PresetsPort; onClose: () => void }) {
  const id = useId();
  const [name, setName] = useState(text(entry.field.label, 'fr', 'Préréglage'));
  const [scope, setScope] = useState<'all_forms' | 'listed_forms'>('all_forms');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const source = (entry.presentation as { option_source?: { object_type: string; field_id: string } } | undefined)?.option_source;
  const save = async (): Promise<void> => {
    if (port.save === undefined) return;
    setBusy(true);
    try {
      await port.save(presetBodyFrom(entry, { label: { fr: name.trim() }, scope }));
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      size="md"
      title="Enregistrer comme préréglage"
      subtitle={text(entry.field.label, 'fr', 'Champ')}
      onClose={onClose}
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: 'Enregistrer…', busy, disabled: port.save === undefined || name.trim() === '', ...(port.save === undefined ? { title: port.saveUnavailable } : {}), onClick: () => void save() }} />}
    >
      <div className="ay-v2-preset">
        <div className="ay-props__row">
          <label htmlFor={id} className="ay-field__label">Nom du préréglage</label>
          <input id={id} type="text" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <h3 className="ay-v2-subtitle">Ce que le préréglage emporte</h3>
        <ul className="ay-v2-carry">
          {summary(entry).map((line) => (
            <li key={line.label}>
              <Icon name="check" size={14} />
              <span>
                <strong>{line.label}</strong>
                <span className="ay-studio__muted">{line.detail}</span>
              </span>
            </li>
          ))}
          {source !== undefined && (
            <li>
              <Icon name="link" size={14} />
              <span>
                <strong>Relié à la donnée</strong>
                <span className="ay-studio__muted">Choix de l’objet du modèle</span>
              </span>
            </li>
          )}
        </ul>
        <div className="ay-v2-line">
          <span className="ay-v2-line__label">Proposé dans</span>
          <span className="ay-studio__seg ay-v2-seg" role="radiogroup" aria-label="Proposé dans">
            <button type="button" role="radio" aria-checked={scope === 'all_forms'} onClick={() => setScope('all_forms')}>Tous les formulaires</button>
            <button type="button" role="radio" aria-checked={scope === 'listed_forms'} onClick={() => setScope('listed_forms')}>Ce formulaire</button>
          </span>
        </div>
        <p className="ay-v2-pill">Propagation automatique</p>
        {port.save === undefined && <p className="ay-studio__muted" role="note"><Icon name="info" size={12} /> {port.saveUnavailable}</p>}
        {error !== null && <p className="ay-studio__refusal" role="alert">{error}</p>}
      </div>
    </Dialog>
  );
}

const shown = (value: string | undefined): string => {
  if (value === undefined) return '—';
  try {
    const parsed = JSON.parse(value) as unknown;
    if (typeof parsed === 'number') return String(parsed);
    if (typeof parsed === 'boolean') return parsed ? 'oui' : 'non';
    // Jamais un identifiant, une référence ni une expression à l'écran : un mot suffit.
    if (typeof parsed === 'string') return /^[0-9a-f]{24}$/.test(parsed) ? 'image' : /[$_{}]/.test(parsed) || parsed.length > 30 ? 'réglé' : parsed;
    if (Array.isArray(parsed)) return `${parsed.length} valeur${parsed.length > 1 ? 's' : ''}`;
  } catch {
    // Rien : la valeur reste montrée comme un réglage.
  }
  return 'réglage';
};

/** Réglages qui changent entre le préréglage publié et l'envisagé, en mots. */
export function presetChanges(current: FieldPresetBody, proposed: FieldPresetBody): Array<{ label: string; before: string; after: string }> {
  const before = presetProjection(current);
  const after = presetProjection(proposed);
  const pointers = [...new Set([...before.keys(), ...after.keys()])].filter((pointer) => before.get(pointer) !== after.get(pointer)).sort();
  const out: Array<{ label: string; before: string; after: string }> = [];
  for (const pointer of pointers) {
    const label = settingLabel(pointer);
    const found = out.find((line) => line.label === label);
    if (found === undefined) out.push({ label, before: shown(before.get(pointer)), after: shown(after.get(pointer)) });
    else {
      found.before = `${found.before} · ${shown(before.get(pointer))}`;
      found.after = `${found.after} · ${shown(after.get(pointer))}`;
    }
  }
  return out;
}

export function PresetImpactsDialog({ presetId, current, proposed, port, onClose }: { presetId: string; current: FieldPresetBody; proposed: FieldPresetBody; port: PresetsPort; onClose: () => void }) {
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    port.usages(presetId, proposed).then((result) => alive && setReport(result), (failure: unknown) => alive && setError(failure instanceof Error ? failure.message : 'Usages illisibles.'));
    return () => {
      alive = false;
    };
  }, [port, presetId, proposed]);
  const changes = presetChanges(current, proposed);
  const follows = report?.usages.filter((usage) => usage.outcome === 'follows').length ?? 0;
  const keeps = report?.usages.filter((usage) => usage.outcome === 'keeps').length ?? 0;
  return (
    <Dialog
      open
      size="lg"
      title={`Impacts du changement · préréglage « ${text(current.label, 'fr', 'Préréglage')} »`}
      subtitle={report === null ? 'Lecture des usages…' : `${report.counts.forms} formulaire${report.counts.forms > 1 ? 's' : ''}`}
      onClose={onClose}
      footer={
        <div className="ay-v2-dialog-foot">
          {report !== null && <strong className="ay-v2-nowrap">{follows} suivent · {keeps} gardent</strong>}
          {report !== null && <span className="ay-v2-pill" title={report.computedBy === 'server' ? 'Calculé par le serveur' : 'Calculé par ce poste depuis les gabarits actifs'}>{report.computedBy === 'server' ? 'Serveur' : 'Calcul du poste'}</span>}
          <span className="ay-studio__spacer" />
          <DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: 'Enregistrer', disabled: port.save === undefined || report === null, ...(port.save === undefined ? { title: port.saveUnavailable } : {}), onClick: () => void port.save?.(proposed, presetId).then(onClose) }} />
        </div>
      }
    >
      <div className="ay-v2-impacts">
        <ul className="ay-v2-impacts__summary">
          <li>
            <span className="ay-studio__muted">Composant · ne change pas</span>
            <strong>{widgetWord(proposed.widget)}</strong>
            <span className="ay-v2-pill">Core</span>
          </li>
          {changes.map((change) => (
            <li key={change.label}>
              <span className="ay-studio__muted">Réglage du préréglage · change</span>
              <strong>{change.label}</strong>
              <span><s>{change.before}</s> → {change.after}</span>
            </li>
          ))}
          {changes.length === 0 && <li className="ay-studio-empty">Aucun réglage ne change.</li>}
        </ul>
        {error !== null && <p className="ay-studio__refusal" role="alert">{error}</p>}
        {report !== null && (
          <table className="ay-v2-impacts__table">
            <thead>
              <tr>
                <th scope="col">Formulaire</th>
                <th scope="col">Élément</th>
                <th scope="col">Après l’enregistrement</th>
              </tr>
            </thead>
            <tbody>
              {report.usages.map((usage) => (
                <tr key={`${usage.template_id}|${usage.section_id}|${usage.field_id}`} className={`is-${usage.outcome ?? 'unchanged'}`}>
                  <td>{text(usage.template_label, 'fr', 'Formulaire')}{usage.status === 'draft' ? ' (brouillon)' : ''}</td>
                  <td>{text(usage.element_label, 'fr', 'Champ')}</td>
                  <td>{usage.outcome === 'keeps' ? `gardera son réglage, modifié ici` : usage.outcome === 'follows' ? 'suivra le nouveau réglage' : 'inchangé'}</td>
                </tr>
              ))}
              {report.usages.length === 0 && (
                <tr>
                  <td colSpan={3} className="ay-studio-empty">Aucun formulaire ne cite ce préréglage.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {report?.truncated === true && <p className="ay-studio__muted" role="note">Liste coupée : d’autres usages existent.</p>}
      </div>
    </Dialog>
  );
}

/** Corps envisagé d'un préréglage : ses réglages remplacés par ceux de l'entrée (« Mettre à jour le préréglage »). */
export const proposedFrom = (entry: Entry, current: FieldPresetBody): FieldPresetBody => presetBodyFrom(entry, { label: current.label as Record<string, string>, scope: current.availability?.scope ?? 'all_forms', ...(current.availability?.forms === undefined ? {} : { forms: current.availability.forms }), base: current });
